import { BooleanInput } from '@angular/cdk/coercion';
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroupDirective, NgControl } from '@angular/forms';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'joii-signature',
  providers: [provideValueAccessor(SignatureComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <canvas
      #canvasRef
      [id]="inputId()"
      [attr.aria-label]="ariaLabel() || label()"
      [attr.aria-disabled]="isDisabled()"
      [attr.aria-readonly]="readonly()"
      [attr.aria-invalid]="hasError"
      [attr.aria-describedby]="hasError && displayError() ? errorId() : null"
      role="img"
      tabindex="0"
      (focus)="onFocus()"
      (blur)="onBlur()"
    ></canvas>

    @if (!hasSignature()) {
      <label [for]="inputId()">{{ hasError && displayError() ? displayError() : label() }}</label>
    }

    @if (hasSignature() && canEdit()) {
      <button type="button" (click)="clear()">{{ clearLabel() }}</button>
    }
  `,
  styles: `
    :host {
      position: relative;
      display: block;
      width: 100%;
      height: var(--joii-signature-height);
      border-radius: var(--Vetai-Radius-2);
      padding: var(--Vetai-Spacing-3);
      border: 1px solid var(--Vetai-Form_controls--Border_Default);
      background-color: var(--Vetai-Form_controls--Background_Default);
      outline: none;
      box-sizing: border-box;
    }

    :host([data-focused='true']),
    :host([data-hovered='true']) {
      background-color: var(--Vetai-Form_controls--Background_Hover);
      border-color: var(--Vetai-Form_controls--Border_Hover);
    }

    :host([data-active='true'][data-focused='true']) {
      border-color: var(--Vetai-Form_controls--Border_Active);
    }

    :host([data-readonly='true']),
    :host([data-disabled='true']),
    :host([data-readonly='true'][data-focused='true']),
    :host([data-disabled='true'][data-focused='true']),
    :host([data-readonly='true'][data-hovered='true']),
    :host([data-disabled='true'][data-hovered='true']) {
      border-color: var(--Vetai-Form_controls--Border_Default);
      background-color: var(--Vetai-Backgrounds--Tint_2);
      cursor: not-allowed;
    }

    canvas {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
      outline: none;
      background: transparent;
      touch-action: none;
      cursor: crosshair;
    }

    :host([data-readonly='true']) canvas,
    :host([data-disabled='true']) canvas {
      cursor: not-allowed;
    }

    :host([data-error='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Default);
    }

    :host([data-error='true'][data-focused='true']),
    :host([data-error='true'][data-hovered='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Hover);
    }

    :host([data-error='true'][data-active='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Active);
    }

    label {
      position: absolute;
      top: var(--Vetai-Spacing-3);
      left: var(--Vetai-Spacing-3);
      color: var(--Vetai-Form_controls--Placeholder);
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T5);
      pointer-events: none;
      user-select: none;
    }

    :host([data-error='true']) label {
      color: var(--Vetai-Status_packages--Error--Default);
    }

    .error-message {
      position: absolute;
      left: var(--Vetai-Spacing-3);
      bottom: var(--Vetai-Spacing-2);
      margin: 0;
      color: var(--Vetai-Status_packages--Error--Default);
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T6);
      pointer-events: none;
    }

    button {
      position: absolute;
      top: var(--Vetai-Spacing-2);
      right: var(--Vetai-Spacing-2);
      border: 0;
      border-radius: var(--Vetai-Radius-1);
      padding: var(--Vetai-Spacing-1) var(--Vetai-Spacing-2);
      background: var(--Vetai-Backgrounds--Tint_2);
      color: var(--Vetai-Texts--Base);
      font-family: var(--Vetai-Font-Family);
      font-size: var(--Vetai-T6);
      cursor: pointer;
    }

    button:hover,
    button:focus-visible {
      background: var(--Vetai-Form_controls--Background_Hover);
      outline: 1px solid var(--Vetai-Form_controls--Border_Active);
    }
  `,
  host: {
    '[attr.data-focused]': 'focused() ? "true" : null',
    '[attr.data-hovered]': 'hovered() ? "true" : null',
    '[attr.data-active]': 'hasSignature() ? "true" : null',
    '[attr.data-disabled]': 'isDisabled() ? "true" : null',
    '[attr.data-readonly]': 'readonly() ? "true" : null',
    '[attr.data-error]': 'hasError ? "true" : null',
    '[style.--joii-signature-height]': 'height() + "px"',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class SignatureComponent implements AfterViewInit {
  readonly value = model<string>('');
  readonly label = input<string>('Click to sign');
  readonly inputId = input<string>(`joii-signature-${Math.random().toString(36).slice(2, 11)}`);
  readonly height = input<number>(160);
  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly readonly = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly ariaLabel = input<string | undefined>();
  readonly clearLabel = input<string>('Clear signature');
  readonly errorMessage = input<string | undefined>();
  readonly errorId = input<string>(`joii-signature-error-${Math.random().toString(36).slice(2, 11)}`);

  readonly cleared = output<void>();

  protected readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvasRef');
  protected readonly formDisabled = signal(false);
  protected readonly focused = signal(false);
  protected readonly hovered = signal(false);
  protected readonly hasSignature = computed(() => this.value().length > 0);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly canEdit = computed(() => !this.isDisabled() && !this.readonly());

  private readonly injector = inject(Injector);
  private readonly formDirective = inject(FormGroupDirective, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private signaturePad?: SignaturePad;
  private resizeObserver?: ResizeObserver;
  private themeObserver?: MutationObserver;
  private onChange?: ChangeFn<string | undefined>;
  private onTouched?: TouchedFn;
  private readonly canvasReady = signal(false);
  private readonly controlStatusVersion = signal(0);
  private lastRenderedValue = '';
  private previewRenderId = 0;

  readonly displayError = computed(() => {
    const msg = this.errorMessage();
    if (msg) return msg;
    this.controlStatusVersion();
    const control = this.injector.get(NgControl, null)?.control;
    if (!control?.errors) return '';
    return this.getControlErrorMessageFromErrors(control.errors);
  });

  /** True when the attached FormControl is invalid and validation should be shown. */
  get hasError(): boolean {
    const control = this.injector.get(NgControl, null)?.control;
    const message = this.displayError();
    if (!control) return message.length > 0;
    const showError = this.getShowError(this.focused(), control, this.formDirective ?? undefined);
    return !!(control.invalid && showError && message.length > 0);
  }

  constructor() {
    afterNextRender(() => {
      const ngControl = this.injector.get(NgControl, null);
      const control = ngControl?.control;
      if (!control) return;
      const bumpVersion = () => {
        this.controlStatusVersion.update((v) => v + 1);
        this.cdr.markForCheck();
      };
      control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      this.formDirective?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
    });

    afterNextRender(() => {
      this.initSignaturePad();
    });

    effect(() => {
      this.canEdit();
      this.syncSignaturePadInteractivity();
    });

    effect(() => {
      const value = this.value();
      this.canvasReady();
      queueMicrotask(() => this.renderValue(value));
    });

    this.destroyRef.onDestroy(() => {
      this.signaturePad?.removeEventListener('endStroke', this.handleEndStroke);
      this.signaturePad?.off();
      this.resizeObserver?.disconnect();
      this.themeObserver?.disconnect();
    });
  }

  ngAfterViewInit(): void {
    this.initSignaturePad();
  }

  writeValue(value: string | null | undefined): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: ChangeFn<string | undefined>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  clear(): void {
    if (!this.canEdit()) return;

    this.signaturePad?.clear();
    this.updateValue('');
    this.onTouched?.();
    this.cleared.emit();
  }

  protected onFocus(): void {
    this.focused.set(true);
  }

  protected onBlur(): void {
    this.focused.set(false);
    this.onTouched?.();
  }

  protected onMouseEnter(): void {
    this.hovered.set(true);
  }

  protected onMouseLeave(): void {
    this.hovered.set(false);
  }

  private initSignaturePad(): void {
    if (this.signaturePad) return;

    const canvas = this.getCanvas();
    if (!canvas) return;

    this.signaturePad = new SignaturePad(canvas, {
      penColor: this.getPenColor(),
      backgroundColor: 'rgba(0,0,0,0)',
    });
    this.signaturePad.addEventListener('endStroke', this.handleEndStroke);
    this.resizeCanvas();
    this.observeCanvasSize(canvas);
    this.observeThemeChanges();
    this.syncSignaturePadInteractivity();
    this.canvasReady.set(true);
  }

  private observeCanvasSize(canvas: HTMLCanvasElement): void {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    this.resizeObserver.observe(canvas);
  }

  private resizeCanvas(): void {
    const canvas = this.getCanvas();
    if (!canvas) return;

    const currentValue = this.value();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);

    if (currentValue && this.signaturePad) {
      void this.renderThemedPreview(currentValue);
    } else {
      this.signaturePad?.clear();
      this.lastRenderedValue = '';
    }
  }

  private renderValue(value: string): void {
    if (!this.signaturePad || value === this.lastRenderedValue) return;

    if (!value) {
      this.previewRenderId++;
      this.signaturePad.clear();
      this.lastRenderedValue = '';
      return;
    }

    void this.renderThemedPreview(value);
  }

  private readonly handleEndStroke = (): void => {
    if (!this.canEdit()) return;

    this.onTouched?.();
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      this.updateValue('');
      return;
    }

    this.updateValue(this.exportBlackOnWhite());
  };

  private updateValue(value: string): void {
    if (!value) {
      this.previewRenderId++;
    }
    this.value.set(value);
    this.lastRenderedValue = value;
    this.onChange?.(value);
  }

  private syncSignaturePadInteractivity(): void {
    if (!this.signaturePad) return;

    if (this.canEdit()) {
      this.signaturePad.on();
    } else {
      this.signaturePad.off();
    }
  }

  private observeThemeChanges(): void {
    if (!isPlatformBrowser(this.platformId) || typeof MutationObserver === 'undefined' || this.themeObserver) return;

    const themeElement = this.host.nativeElement.closest('[data-theme]') ?? document.documentElement;
    const updateTheme = (): void => {
      this.syncPenColor();

      const value = this.value();
      if (value) {
        void this.renderThemedPreview(value);
      }
    };

    updateTheme();
    this.themeObserver = new MutationObserver(updateTheme);
    this.themeObserver.observe(themeElement, { attributes: true, attributeFilter: ['data-theme'] });
  }

  private syncPenColor(): void {
    if (!this.signaturePad) return;

    this.signaturePad.penColor = this.getPenColor();
  }

  private exportBlackOnWhite(): string {
    const canvas = this.getCanvas();
    if (!canvas) return '';

    const inkCanvas = document.createElement('canvas');
    inkCanvas.width = canvas.width;
    inkCanvas.height = canvas.height;

    const inkContext = inkCanvas.getContext('2d');
    if (!inkContext) return '';

    inkContext.drawImage(canvas, 0, 0);
    inkContext.globalCompositeOperation = 'source-in';
    inkContext.fillStyle = '#000000';
    inkContext.fillRect(0, 0, inkCanvas.width, inkCanvas.height);

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;

    const exportContext = exportCanvas.getContext('2d');
    if (!exportContext) return '';

    exportContext.fillStyle = '#ffffff';
    exportContext.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportContext.drawImage(inkCanvas, 0, 0);

    return exportCanvas.toDataURL('image/png');
  }

  private async renderThemedPreview(value: string): Promise<void> {
    if (!this.signaturePad) return;

    const renderId = ++this.previewRenderId;
    const canvas = this.getCanvas();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = canvas?.offsetWidth;
    const height = canvas?.offsetHeight;

    try {
      const previewDataUrl = await this.createThemedPreviewDataUrl(value);
      if (renderId !== this.previewRenderId || this.value() !== value) return;

      await this.signaturePad.fromDataURL(previewDataUrl, { ratio, width, height });
    } catch {
      if (renderId !== this.previewRenderId || this.value() !== value) return;

      await this.signaturePad.fromDataURL(value, { ratio, width, height });
    }

    this.lastRenderedValue = value;
  }

  private async createThemedPreviewDataUrl(value: string): Promise<string> {
    const image = await this.loadImage(value);
    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = image.naturalWidth || image.width;
    previewCanvas.height = image.naturalHeight || image.height;

    const context = previewCanvas.getContext('2d');
    if (!context) return value;

    context.drawImage(image, 0, 0, previewCanvas.width, previewCanvas.height);

    const imageData = context.getImageData(0, 0, previewCanvas.width, previewCanvas.height);
    const [red, green, blue] = this.getPenColorRgb();

    for (let i = 0; i < imageData.data.length; i += 4) {
      const isWhite = imageData.data[i] > 245 && imageData.data[i + 1] > 245 && imageData.data[i + 2] > 245;

      if (isWhite) {
        imageData.data[i + 3] = 0;
      } else if (imageData.data[i + 3] > 0) {
        imageData.data[i] = red;
        imageData.data[i + 1] = green;
        imageData.data[i + 2] = blue;
      }
    }

    context.putImageData(imageData, 0, 0);

    return previewCanvas.toDataURL('image/png');
  }

  private loadImage(value: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load signature image'));
      image.src = value;
    });
  }

  private getPenColor(): string {
    return getComputedStyle(this.host.nativeElement).getPropertyValue('--Vetai-Texts--Base').trim() || 'black';
  }

  private getPenColorRgb(): [number, number, number] {
    const color = this.getPenColor();
    const match = /^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(color);

    if (!match) {
      return [0, 0, 0];
    }

    return [Number(match[1]), Number(match[2]), Number(match[3])];
  }

  private getCanvas(): HTMLCanvasElement | null {
    return this.canvasRef()?.nativeElement ?? this.host.nativeElement.querySelector('canvas');
  }

  private getShowError(focused: boolean, control?: AbstractControl, formGroupDirective?: FormGroupDirective): boolean {
    if (formGroupDirective) {
      return (
        (formGroupDirective.submitted ?? false) || (focused && (control?.dirty ?? false) && (control?.touched ?? false))
      );
    }
    return focused || (control?.touched ?? false) || (control?.dirty ?? false);
  }

  private getControlErrorMessageFromErrors(errors: Record<string, unknown>): string {
    for (const key of Object.keys(errors)) {
      const value = errors[key];
      if (value && typeof value === 'object' && typeof (value as { message?: string }).message === 'string') {
        return (value as { message: string }).message;
      }
    }
    if (errors['required'] ?? errors['requiredTrue']) return 'Field is required';
    return 'Field is invalid';
  }
}
